import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { AppErrorBoundary } from '@ui/components/AppErrorBoundary';
import {
  captureAttribution,
  getStoredAttribution,
  attributionToPosthogProps,
} from './utils/captureAttribution';
import { config } from './config';

// Transitional shim: config is baked at build time (see config.ts); existing
// read sites (incl. env.ts ensureEnv) still access window.__ENV__. New code
// should import { config }.
window.__ENV__ = config;

// Capture attribution params before anything else renders
captureAttribution();

const posthogKey = config.POSTHOG_PUBLIC_KEY;
const posthogHost = config.POSTHOG_PUBLIC_HOST || 'https://us.i.posthog.com';

const options = {
  api_host: posthogHost,
  capture_exceptions: true,
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: '.ph-mask',
  },
  // PostHog initializes after the app boots/routes, so its own $initial_utm_*
  // capture misses the ad-click query string. captureAttribution() (run above,
  // synchronously, before render) reliably has it — register it as super-
  // properties here so every event carries the campaign params. register()
  // does not create a person; first-touch person props are set at identify
  // (see PostHogIdentify).
  loaded: (ph: typeof posthog) => {
    const { superProps } = attributionToPosthogProps(getStoredAttribution());
    if (Object.keys(superProps).length > 0) ph.register(superProps);
  },
};

const reportError = (error: Error) => {
  if (posthogKey) posthog.captureException(error);
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  posthogKey ? (
    <PostHogProvider apiKey={posthogKey} options={options}>
      <AppErrorBoundary appName="seeker" onError={reportError}>
        <App />
      </AppErrorBoundary>
    </PostHogProvider>
  ) : (
    <AppErrorBoundary appName="seeker">
      <App />
    </AppErrorBoundary>
  )
);
